import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
} from "services/api";
// import { LIMIT_DATA_PER_PAGE } from "utils/constants";
import { buildQueryString } from "utils/queryHelpers";

const CategoryContext = createContext();

export const useCategory = () => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error("useCategory must be used within a CategoryProvider");
  }

  const fetchOptions = async (inputValue) => {
    const response = await getRequest(
      `/inventory-category?search=${inputValue}`
    );
    if (response.success) {
      return response.data.map((category) => ({
        value: category.id,
        label: category.name,
      }));
    }
    return [];
  };

  return { ...context, fetchOptions };
};

export const CategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
  });

  const [
    categoryStats,
    // , setCategoryStats
  ] = useState({
    total: 0,
    colored: 0,
    loading: false,
    error: null,
  });

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    limit: 10,
    all: 1,
    // include: "status",
  });

  const fetchCategories = useCallback(async () => {
    setCategories((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`/inventory-category${queryString}`);
      if (response.success) {
        setCategories({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.pagination?.last_page,
        }));
      }
    } catch (error) {
      setCategories({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [queryString]);

  // const fetchCategoryStats = useCallback(async () => {
  //   setCategoryStats((prevState) => ({
  //     ...prevState,
  //     loading: true,
  //   }));

  //   try {
  //     const response = await getRequest("/inventory-category/stats");
  //     if (response.success) {
  //       setCategoryStats({
  //         total: response.data.total || 0,
  //         colored: response.data.colored || 0,
  //         loading: false,
  //         error: null,
  //       });
  //     }
  //   } catch (error) {
  //     setCategoryStats({
  //       total: 0,
  //       colored: 0,
  //       loading: false,
  //       error: error.message,
  //     });
  //   }
  // }, []);

  // Initial load and when dependencies change
  useEffect(() => {
    fetchCategories();
    // fetchCategoryStats();
  }, [
    fetchCategories,
    // , fetchCategoryStats
  ]);

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      console.log("search", e.target.value);
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
      }));
    }
  };

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = categories.pagination?.last_page || 1;

  const Pagination = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  const createCategory = useCallback(
    async (categoryData) => {
      try {
        const response = await postRequest("/inventory-category", categoryData);
        if (response.success) {
          fetchCategories(); // Refresh the list
          return { success: true };
        }
        return { success: false, error: response.message };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },
    [fetchCategories]
  );

  const updateCategory = useCallback(
    async (id, categoryData) => {
      try {
        const response = await patchRequest(
          `/inventory-category/${id}`,
          categoryData
        );
        if (response.success) {
          fetchCategories(); // Refresh the list
          return { success: true };
        }
        return { success: false, error: response.message };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },
    [fetchCategories]
  );

  const deleteCategory = useCallback(
    async (id) => {
      try {
        const response = await deleteRequest(`/inventory-category/${id}`);
        if (response.success) {
          fetchCategories(); // Refresh the list
          return { success: true };
        }
        return { success: false, error: response.message };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },
    [fetchCategories]
  );

  const value = {
    categories: categories.data,
    loading: categories.loading,
    error: categories.error,
    pagination: categories.pagination,
    categoryStats,
    refetch: fetchCategories,
    // refetchStats: fetchCategoryStats,
    filterOptions,
    handleSearch,
    createCategory,
    updateCategory,
    deleteCategory,
    Pagination,
  };

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  );
};
